import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { Scale, ArrowLeft, ArrowRight, Check, Upload } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../components/auth/AuthComponents';
import { apiRequest, normalizeExternalUrl } from '../utils/api';
import { COUNTRIES } from '../constants/countries';
import { LANGUAGE_OPTIONS } from '../constants/languages';
import MultiSelect from '../components/shared/MultiSelect';
import UnauthorizedBox from '../components/shared/UnauthorizedBox';
import { isLawyerRole, isAdminRole, canBrowseFindLawyer } from '../utils/userPermissions';

const PRACTICE_OPTIONS = [
  'Employment Law',
  'Corporate Law',
  'Contract Law',
  'Immigration Law',
  'Intellectual Property',
  'Family Law',
  'Real Estate',
  'Tax Law',
  'Litigation',
  'Criminal Law',
  'Data Privacy',
  'Startup & SME Advisory',
];

const STEPS = [
  { id: 1, title: 'Profile', subtitle: 'About your practice' },
  { id: 2, title: 'Location', subtitle: 'Credentials' },
  { id: 3, title: 'Contact', subtitle: 'Photo, cover & links' },
];

const emptyForm = {
  full_name: '',
  email: '',
  password: '',
  display_name: '',
  firm_name: '',
  headline: '',
  bio: '',
  practice_areas: [],
  country: 'Nigeria',
  state: '',
  city: '',
  bar_number: '',
  nin_number: '',
  call_to_bar_certificate_url: '',
  years_experience: '',
  languages: '',
  phone: '',
  website: '',
  linkedin_url: '',
  photo_url: '',
  cover_image_url: '',
  show_contact_on_profile: false,
};

function lawyerApiErrorMessage(message) {
  const text = message || '';
  if (text === 'Not Found' || text.startsWith('HTTP 404')) {
    return 'Lawyer listing service is not available yet. Please try again shortly or contact support if this continues.';
  }
  return text || 'Something went wrong';
}

function RequiredStar() {
  return <span className="text-red-400 ml-1" aria-hidden="true">*</span>;
}

export default function LawyerApplyPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState('');
  const [certificateFile, setCertificateFile] = useState(null);
  const [existingStatus, setExistingStatus] = useState(null);

  useEffect(() => {
    return () => {
      if (photoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(photoPreview);
      }
      if (coverPreview.startsWith('blob:')) {
        URL.revokeObjectURL(coverPreview);
      }
    };
  }, [photoPreview, coverPreview]);

  useEffect(() => {
    if (isAuthenticated && user) {
      setForm((prev) => ({
        ...prev,
        full_name: user.full_name || '',
        email: user.email || '',
        display_name: user.full_name || '',
      }));
      apiRequest('lawyers/me')
        .then((profile) => {
          if (!profile) {
            setExistingStatus(null);
            return;
          }
          setExistingStatus(profile.status);
          setForm((prev) => ({
            ...prev,
            display_name: profile.display_name || prev.display_name,
            firm_name: profile.firm_name || '',
            headline: profile.headline || '',
            bio: profile.bio || '',
            practice_areas: profile.practice_areas || [],
            country: profile.country || 'Nigeria',
            state: profile.state || '',
            city: profile.city || '',
            bar_number: profile.bar_number || '',
            nin_number: profile.nin_number || '',
            call_to_bar_certificate_url: profile.call_to_bar_certificate_url || '',
            years_experience: profile.years_experience != null ? String(profile.years_experience) : '',
            languages: (profile.languages || []).join(', '),
            phone: profile.phone || '',
            website: profile.website || '',
            linkedin_url: profile.linkedin_url || '',
            photo_url: profile.photo_url || '',
            cover_image_url: profile.cover_image_url || '',
            show_contact_on_profile: profile.show_contact_on_profile || false,
          }));
        })
        .catch(() => setExistingStatus(null));
    }
  }, [isAuthenticated, user]);

  const togglePractice = (area) => {
    setForm((prev) => {
      const set = new Set(prev.practice_areas);
      if (set.has(area)) set.delete(area);
      else set.add(area);
      return { ...prev, practice_areas: [...set] };
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const validateImageFile = (file, maxMb) => {
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      toast.error('Image must be JPEG, PNG, WebP, or GIF');
      return false;
    }
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(`Image must be ${maxMb} MB or smaller`);
      return false;
    }
    return true;
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!validateImageFile(file, 5)) {
      e.target.value = '';
      return;
    }

    if (photoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(photoPreview);
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setForm((prev) => ({ ...prev, photo_url: '' }));
    e.target.value = '';
  };

  const clearPhoto = () => {
    if (photoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(photoPreview);
    }
    setPhotoFile(null);
    setPhotoPreview('');
    setForm((prev) => ({ ...prev, photo_url: '' }));
  };

  const handleCoverChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!validateImageFile(file, 8)) {
      e.target.value = '';
      return;
    }

    if (coverPreview.startsWith('blob:')) {
      URL.revokeObjectURL(coverPreview);
    }
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
    setForm((prev) => ({ ...prev, cover_image_url: '' }));
    e.target.value = '';
  };

  const clearCover = () => {
    if (coverPreview.startsWith('blob:')) {
      URL.revokeObjectURL(coverPreview);
    }
    setCoverFile(null);
    setCoverPreview('');
    setForm((prev) => ({ ...prev, cover_image_url: '' }));
  };

  const validateCertificateFile = (file) => {
    const allowed = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/webp',
      'image/gif',
    ];
    if (!allowed.includes(file.type)) {
      toast.error('Call to Bar certificate must be PDF, JPEG, PNG, WebP, or GIF');
      return false;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Certificate must be 10 MB or smaller');
      return false;
    }
    return true;
  };

  const handleCertificateChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!validateCertificateFile(file)) {
      e.target.value = '';
      return;
    }
    setCertificateFile(file);
    setForm((prev) => ({ ...prev, call_to_bar_certificate_url: '' }));
    e.target.value = '';
  };

  const clearCertificate = () => {
    setCertificateFile(null);
    setForm((prev) => ({ ...prev, call_to_bar_certificate_url: '' }));
  };

  const validateStep = (currentStep) => {
    if (currentStep === 1) {
      if (!isAuthenticated) {
        if (!form.full_name.trim()) {
          toast.error('Full name is required');
          return false;
        }
        if (!form.email.trim()) {
          toast.error('Email is required');
          return false;
        }
        if (!form.password || form.password.length < 8) {
          toast.error('Password must be at least 8 characters');
          return false;
        }
      }
      if (!form.display_name.trim()) {
        toast.error('Display name is required');
        return false;
      }
      if (!form.headline.trim()) {
        toast.error('Headline is required');
        return false;
      }
      if (!form.bio.trim() || form.bio.trim().length < 40) {
        toast.error('Bio must be at least 40 characters');
        return false;
      }
      if (form.practice_areas.length < 1) {
        toast.error('Select at least one practice area');
        return false;
      }
    }
    if (currentStep === 2 && !(existingStatus === 'approved' && isAuthenticated)) {
      if (!form.bar_number.trim()) {
        toast.error('SCN is required');
        return false;
      }
      const nin = form.nin_number.trim();
      if (!nin) {
        toast.error('NIN number is required');
        return false;
      }
      if (!/^\d{11}$/.test(nin)) {
        toast.error('NIN number must be exactly 11 digits');
        return false;
      }
      if (!certificateFile && !form.call_to_bar_certificate_url) {
        toast.error('Call to Bar certificate is required');
        return false;
      }
    }
    return true;
  };

  const goNext = () => {
    if (!validateStep(step)) return;
    // Defer so the same click cannot land on the Submit button after re-render.
    window.setTimeout(() => setStep((s) => Math.min(3, s + 1)), 0);
  };

  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const submitApplication = async () => {
    if (step !== 3 || submitting) return;
    if (!validateStep(1)) {
      setStep(1);
      return;
    }
    if (!validateStep(2)) {
      setStep(2);
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        display_name: form.display_name.trim(),
        firm_name: form.firm_name.trim() || null,
        headline: form.headline.trim(),
        bio: form.bio.trim(),
        practice_areas: form.practice_areas,
        country: form.country || null,
        state: form.state.trim() || null,
        city: form.city.trim() || null,
        bar_number: form.bar_number.trim() || null,
        nin_number: form.nin_number.trim() || null,
        call_to_bar_certificate_url: form.call_to_bar_certificate_url.trim() || null,
        years_experience: form.years_experience ? Number(form.years_experience) : null,
        languages: form.languages
          ? form.languages.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
        phone: form.phone.trim() || null,
        website: form.website.trim() ? normalizeExternalUrl(form.website.trim()) : null,
        linkedin_url: form.linkedin_url.trim() ? normalizeExternalUrl(form.linkedin_url.trim()) : null,
        photo_url: form.photo_url.trim() || null,
        cover_image_url: form.cover_image_url.trim() || null,
        show_contact_on_profile: form.show_contact_on_profile,
      };

      if (!isAuthenticated) {
        body.email = form.email.trim();
        body.password = form.password;
        body.full_name = form.full_name.trim();
      }

      if (existingStatus === 'approved' && isAuthenticated) {
        if (photoFile) {
          const fd = new FormData();
          fd.append('photo', photoFile);
          const uploaded = await apiRequest('lawyers/photo', { method: 'POST', body: fd });
          body.photo_url = uploaded.url;
        }
        if (coverFile) {
          const fd = new FormData();
          fd.append('cover', coverFile);
          const uploaded = await apiRequest('lawyers/cover', { method: 'POST', body: fd });
          body.cover_image_url = uploaded.url;
        }
        await apiRequest('lawyers/me', { method: 'PUT', body });
        toast.success('Profile updated');
      } else {
        let res;
        if (photoFile || coverFile || certificateFile) {
          const fd = new FormData();
          fd.append('payload', JSON.stringify(body));
          if (photoFile) fd.append('photo', photoFile);
          if (coverFile) fd.append('cover', coverFile);
          if (certificateFile) fd.append('call_to_bar_certificate', certificateFile);
          res = await apiRequest('lawyers/apply', { method: 'POST', body: fd });
        } else {
          res = await apiRequest('lawyers/apply', { method: 'POST', body });
        }
        toast.success(res.message || 'Application submitted');
      }
      if (isAuthenticated) {
        navigate('/profile?tab=lawyer');
      } else {
        navigate('/login');
      }
    } catch (err) {
      toast.error(lawyerApiErrorMessage(err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const previewSrc = photoPreview || form.photo_url || '';
  const coverPreviewSrc = coverPreview || form.cover_image_url || '';
  const isApprovedEdit = existingStatus === 'approved' && isAuthenticated;

  if (isAuthenticated && user && !isLawyerRole(user.role) && !isAdminRole(user.role)) {
    return (
      <UnauthorizedBox
        title="Unauthorized"
        message="Listing a lawyer profile is only available to lawyer accounts."
        upgradeMessage=""
        showUpgrade={false}
        backToDashboard
      />
    );
  }

  const inputClass =
    'w-full px-4 py-2.5 rounded-xl themed-input border border-[var(--color-border)]/40 text-[var(--color-text)] placeholder-[var(--color-placeholder)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]/40 focus:border-[var(--color-primary)]/50';
  const lockedInputClass =
    'w-full px-4 py-2.5 rounded-xl bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-text-muted)] cursor-not-allowed';
  const labelClass = 'block text-sm font-medium text-[var(--color-text-secondary)] mb-1.5';
  const lockedHint = 'Locked after approval — contact support to request a change.';

  return (
    <div className="flex-1 text-[var(--color-text)]">
      <Helmet>
        <title>List Your Practice | Find a Lawyer | DocxsAi</title>
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8 pb-8 sm:pb-12">
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="order-2 min-w-0 flex-1 sm:order-1">
            <div className="inline-flex items-center gap-2 rounded-full bg-[var(--color-primary-soft)] border border-[var(--color-primary)]/30 px-3 py-1 text-sm text-[var(--color-primary)] mb-3">
              <Scale className="w-4 h-4" />
              Lawyer profile
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-2">
              {isApprovedEdit ? 'Edit your listing' : 'List your practice'}
            </h1>
            <p className="text-[var(--color-text-muted)] leading-relaxed max-w-2xl">
              {isApprovedEdit
                ? 'Update your public profile details. Verified fields such as name, location, bar number, and practice areas are locked.'
                : 'Submit your profile for admin review. Once approved, you will get a shareable public page and appear in search results.'}
            </p>
          </div>
          <Link
            to={canBrowseFindLawyer(user?.role) ? '/find-a-lawyer' : '/dashboard'}
            className="order-1 inline-flex shrink-0 items-center gap-2 self-end text-sm text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-text)] sm:order-2 sm:pt-1"
          >
            <ArrowLeft className="w-4 h-4" />
            {canBrowseFindLawyer(user?.role) ? 'Find a Lawyer' : 'Dashboard'}
          </Link>
        </div>

        {/* Step indicator */}
        <div className="mb-6 overflow-x-auto scrollbar-thin">
          <div className="flex gap-2 sm:gap-4 min-w-max sm:grid sm:min-w-0 sm:grid-cols-3">
          {STEPS.map((s) => {
            const done = step > s.id;
            const active = step === s.id;
            return (
              <div
                key={s.id}
                className={`rounded-xl border px-3 py-3 sm:px-4 transition-colors w-[148px] sm:w-auto shrink-0 ${
                  active
                    ? 'border-[var(--color-primary)]/50 bg-[var(--color-primary-soft)]'
                    : done
                      ? 'border-emerald-500/30 bg-emerald-500/5'
                      : 'border-[var(--color-border)] elevated-card'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                      active
                        ? 'bg-[var(--color-primary)] text-[var(--color-text-inverse)]'
                        : done
                          ? 'bg-emerald-600 text-[var(--color-text)]'
                          : 'bg-[var(--color-surface-muted)] text-[var(--color-text-muted)]'
                    }`}
                  >
                    {done ? <Check className="w-3.5 h-3.5" /> : s.id}
                  </span>
                  <div className="min-w-0">
                    <p className={`text-sm font-semibold truncate ${active ? 'text-[var(--color-text)]' : 'text-[var(--color-text-secondary)]'}`}>
                      {s.title}
                    </p>
                    <p className="text-[11px] text-[var(--color-text-muted)] truncate hidden sm:block">{s.subtitle}</p>
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        </div>

        <form
          onSubmit={(e) => e.preventDefault()}
          className="rounded-2xl border border-[var(--color-border)] elevated-card backdrop-blur-sm p-5 sm:p-8"
        >
          {step === 1 && (
            <div className="space-y-5">
              {!isAuthenticated ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 pb-5 border-b border-[var(--color-border)]">
                  <h2 className="text-lg font-semibold text-[var(--color-text)] lg:col-span-3">Create your DocxsAi account</h2>
                  <div>
                    <label className={labelClass} htmlFor="full_name">Full name<RequiredStar /></label>
                    <input id="full_name" name="full_name" required value={form.full_name} onChange={handleChange} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass} htmlFor="email">Email<RequiredStar /></label>
                    <input id="email" name="email" type="email" required value={form.email} onChange={handleChange} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass} htmlFor="password">Password<RequiredStar /></label>
                    <input id="password" name="password" type="password" required minLength={8} value={form.password} onChange={handleChange} className={inputClass} />
                  </div>
                </div>
              ) : (
                <p className="text-sm text-[var(--color-text-muted)] pb-2 border-b border-[var(--color-border)]">
                  Signed in as <span className="text-[var(--color-text)]">{user?.email}</span>
                </p>
              )}

              <h2 className="text-lg font-semibold text-[var(--color-text)]">Professional profile</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass} htmlFor="display_name">Display name<RequiredStar /></label>
                  <input
                    id="display_name"
                    name="display_name"
                    required
                    value={form.display_name}
                    onChange={handleChange}
                    disabled={isApprovedEdit}
                    className={isApprovedEdit ? lockedInputClass : inputClass}
                  />
                  {isApprovedEdit && <p className="mt-1 text-xs text-[var(--color-text-muted)]">{lockedHint}</p>}
                </div>
                <div>
                  <label className={labelClass} htmlFor="firm_name">Firm name (optional)</label>
                  <input id="firm_name" name="firm_name" value={form.firm_name} onChange={handleChange} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass} htmlFor="headline">Headline<RequiredStar /></label>
                  <input id="headline" name="headline" required placeholder="e.g. Employment & contract lawyer" value={form.headline} onChange={handleChange} className={inputClass} />
                </div>
              </div>
              <div>
                <label className={labelClass} htmlFor="bio">Bio<RequiredStar /></label>
                <textarea id="bio" name="bio" required rows={3} minLength={40} value={form.bio} onChange={handleChange} className={inputClass} placeholder="Describe your experience, focus areas, and who you help (min. 40 characters)." />
              </div>
              <div>
                <span className={labelClass}>Practice areas<RequiredStar /></span>
                {isApprovedEdit && <p className="text-xs text-[var(--color-text-muted)] mb-2">{lockedHint}</p>}
                <div className="mt-2 -mx-1 px-1 overflow-x-auto scrollbar-thin">
                  <div className="flex flex-nowrap gap-2 pb-1 min-w-max">
                    {PRACTICE_OPTIONS.map((area) => (
                      <button
                        key={area}
                        type="button"
                        disabled={isApprovedEdit}
                        onClick={() => togglePractice(area)}
                        className={`shrink-0 px-3 py-1.5 rounded-full text-sm border transition-colors whitespace-nowrap ${
                          form.practice_areas.includes(area)
                            ? 'bg-[var(--color-primary)] border-[var(--color-primary)] text-[var(--color-text)]'
                            : 'border-[var(--color-border)]/60 text-[var(--color-text-muted)] hover:border-gray-500'
                        } ${isApprovedEdit ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        {area}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <h2 className="text-lg font-semibold text-[var(--color-text)]">Location & credentials</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass} htmlFor="country">Country</label>
                  <select
                    id="country"
                    name="country"
                    value={form.country}
                    onChange={handleChange}
                    disabled={isApprovedEdit}
                    className={isApprovedEdit ? lockedInputClass : inputClass}
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass} htmlFor="state">State / region</label>
                  <input
                    id="state"
                    name="state"
                    value={form.state}
                    onChange={handleChange}
                    disabled={isApprovedEdit}
                    className={isApprovedEdit ? lockedInputClass : inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="city">City</label>
                  <input
                    id="city"
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    disabled={isApprovedEdit}
                    className={isApprovedEdit ? lockedInputClass : inputClass}
                  />
                </div>
              </div>
              {isApprovedEdit && <p className="text-xs text-[var(--color-text-muted)]">{lockedHint}</p>}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass} htmlFor="bar_number">
                    SCN
                    {!isApprovedEdit ? <RequiredStar /> : null}
                  </label>
                  <input
                    id="bar_number"
                    name="bar_number"
                    value={form.bar_number}
                    onChange={handleChange}
                    disabled={isApprovedEdit}
                    placeholder="Supreme Court Number"
                    className={isApprovedEdit ? lockedInputClass : inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="years_experience">Years of experience</label>
                  <input id="years_experience" name="years_experience" type="number" min="0" max="70" value={form.years_experience} onChange={handleChange} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass} htmlFor="languages">Languages</label>
                  <MultiSelect
                    value={form.languages}
                    onChange={(value) => setForm((prev) => ({ ...prev, languages: value }))}
                    options={LANGUAGE_OPTIONS}
                    placeholder="Select languages"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} htmlFor="nin_number">
                    NIN number
                    {!isApprovedEdit ? <RequiredStar /> : null}
                  </label>
                  <input
                    id="nin_number"
                    name="nin_number"
                    value={form.nin_number}
                    onChange={handleChange}
                    disabled={isApprovedEdit}
                    inputMode="numeric"
                    maxLength={11}
                    placeholder="11-digit NIN"
                    className={isApprovedEdit ? lockedInputClass : inputClass}
                  />
                </div>
                <div>
                  <span className={labelClass}>
                    Call to Bar certificate
                    {!isApprovedEdit ? <RequiredStar /> : null}
                  </span>
                  {isApprovedEdit ? (
                    form.call_to_bar_certificate_url ? (
                      <a
                        href={form.call_to_bar_certificate_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-[var(--color-primary)] hover:text-[var(--color-primary)] underline"
                      >
                        View uploaded certificate
                      </a>
                    ) : (
                      <p className="text-sm text-[var(--color-text-muted)]">No certificate on file</p>
                    )
                  ) : (
                    <div className="mt-1 space-y-2">
                      <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-[var(--color-border)] elevated-card px-4 py-6 hover:border-[var(--color-primary)]">
                        <Upload className="w-4 h-4 text-[var(--color-primary)]" />
                        <span className="text-sm text-[var(--color-text-secondary)]">
                          {certificateFile
                            ? certificateFile.name
                            : form.call_to_bar_certificate_url
                              ? 'Replace certificate (PDF or image)'
                              : 'Upload PDF or image'}
                        </span>
                        <input
                          type="file"
                          accept=".pdf,application/pdf,image/jpeg,image/png,image/webp,image/gif"
                          className="hidden"
                          onChange={handleCertificateChange}
                        />
                      </label>
                      {(certificateFile || form.call_to_bar_certificate_url) && (
                        <div className="flex items-center justify-between gap-2 text-xs text-[var(--color-text-muted)]">
                          <span className="truncate">
                            {certificateFile
                              ? certificateFile.name
                              : 'Existing certificate on file'}
                          </span>
                          <button
                            type="button"
                            onClick={clearCertificate}
                            className="text-red-300 hover:text-red-200 shrink-0"
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <h2 className="text-lg font-semibold text-[var(--color-text)]">Contact, photo & cover</h2>
              <div>
                <span className={labelClass}>Profile cover (optional)</span>
                <p className="text-xs text-[var(--color-text-muted)] mb-2">Wide banner shown at the top of your public profile. Recommended 1200×400 or similar.</p>
                <div className="mt-2 overflow-hidden rounded-xl border border-[var(--color-border)]/60 elevated-card">
                  {coverPreviewSrc ? (
                    <img
                      src={coverPreviewSrc}
                      alt="Cover preview"
                      className="h-32 w-full object-cover sm:h-40"
                    />
                  ) : (
                    <div className="flex h-32 w-full items-center justify-center bg-gradient-to-r from-[var(--color-gradient-from)]/60 via-[var(--color-primary)]/40 to-[var(--color-gradient-to)]/60 sm:h-40">
                      <span className="text-sm text-[var(--color-text-muted)]">Default gradient cover</span>
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-3 border-t border-[var(--color-border)] p-3">
                    <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[var(--color-border)]/60 themed-input px-3 py-2 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)] transition-colors">
                      <Upload className="h-4 w-4" />
                      {coverPreviewSrc ? 'Change cover' : 'Choose cover'}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="sr-only"
                        disabled={submitting}
                        onChange={handleCoverChange}
                      />
                    </label>
                    {coverPreviewSrc && (
                      <button
                        type="button"
                        onClick={clearCover}
                        className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
                      >
                        Remove
                      </button>
                    )}
                    <span className="text-xs text-[var(--color-text-muted)]">JPEG, PNG, WebP, or GIF — max 8 MB</span>
                  </div>
                </div>
              </div>
              <div>
                <span className={labelClass}>Profile photo (optional)</span>
                <div className="mt-2 flex flex-wrap items-center gap-4">
                  {previewSrc ? (
                    <img
                      src={previewSrc}
                      alt="Profile preview"
                      className="h-16 w-16 rounded-full object-cover border border-[var(--color-border)]/60"
                    />
                  ) : (
                    <div className="flex h-16 w-16 items-center justify-center rounded-full border border-dashed border-[var(--color-border)]/60 elevated-card text-[var(--color-text-muted)]">
                      <Upload className="h-6 w-6" />
                    </div>
                  )}
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-[var(--color-border)]/60 themed-input px-4 py-2.5 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)] transition-colors">
                    <>
                      <Upload className="h-4 w-4" />
                      {previewSrc ? 'Change photo' : 'Choose photo'}
                    </>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="sr-only"
                      disabled={submitting}
                      onChange={handlePhotoChange}
                    />
                  </label>
                  {previewSrc && (
                    <button
                      type="button"
                      onClick={clearPhoto}
                      className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <p className="mt-1.5 text-xs text-[var(--color-text-muted)]">JPEG, PNG, WebP, or GIF — max 5 MB. Photo is uploaded when you submit.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass} htmlFor="phone">Phone</label>
                  <input id="phone" name="phone" value={form.phone} onChange={handleChange} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass} htmlFor="website">Website</label>
                  <input id="website" name="website" type="text" inputMode="url" placeholder="https://example.com" value={form.website} onChange={handleChange} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass} htmlFor="linkedin_url">LinkedIn URL</label>
                  <input id="linkedin_url" name="linkedin_url" type="text" inputMode="url" placeholder="https://linkedin.com/in/username" value={form.linkedin_url} onChange={handleChange} className={inputClass} />
                </div>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" name="show_contact_on_profile" checked={form.show_contact_on_profile} onChange={handleChange} className="mt-1 rounded border-[var(--color-border)]" />
                <span className="text-sm text-[var(--color-text-secondary)]">Show phone, website, and LinkedIn on my public profile</span>
              </label>
            </div>
          )}

          {/* Wizard controls */}
          <div className="flex items-center justify-between gap-3 mt-8 pt-6 border-t border-[var(--color-border)]">
            {step > 1 ? (
              <button
                type="button"
                onClick={goBack}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[var(--color-border)]/60 text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            ) : (
              <span />
            )}

            {step < 3 ? (
              <button
                type="button"
                onClick={goNext}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] font-semibold transition-colors ml-auto"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={submitApplication}
                disabled={submitting}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] disabled:opacity-60 font-semibold transition-colors ml-auto"
              >
                {submitting ? 'Submitting…' : isApprovedEdit ? 'Save changes' : 'Submit for review'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
